/**
 * I paesi che Hostaly sa gestire, per le tendine del browser.
 *
 * Deve restare allineata a `JURISDICTIONS` in `backend/jurisdictions.py`: il
 * backend rifiuta un paese che non conosce (`400` con l'elenco), quindi una
 * voce in più qui non romperebbe nulla — darebbe solo una tendina che promette
 * un mercato che il prodotto non copre. Il test
 * `backend/tests/test_jurisdictions.py::test_la_tendina_del_frontend_ha_gli_stessi_paesi`
 * confronta le due liste.
 *
 * GB, CH e US sono i primi tre mercati fuori dall'euro: il loro `currency` nel
 * registro è `gbp`, `chf` e `usd`, e non `eur`.
 *
 * Non si carica da `/api/compliance/jurisdictions` di proposito: il form di
 * iscrizione deve funzionare anche con l'API irraggiungibile (una tendina vuota
 * bloccherebbe la registrazione, un default IT no).
 *
 * Il nome c'è in tutte e cinque le lingue dell'interfaccia: `countryName` ripiega
 * sull'inglese per una lingua che non conosce, e una tendina che cambia metà
 * voci è peggio di una tradotta a metà.
 */
export const COUNTRIES = [
  { code: "IT", it: "Italia", en: "Italy", es: "Italia", fr: "Italie", de: "Italien" },
  { code: "ES", it: "Spagna", en: "Spain", es: "España", fr: "Espagne", de: "Spanien" },
  { code: "FR", it: "Francia", en: "France", es: "Francia", fr: "France", de: "Frankreich" },
  { code: "DE", it: "Germania", en: "Germany", es: "Alemania", fr: "Allemagne", de: "Deutschland" },
  { code: "AT", it: "Austria", en: "Austria", es: "Austria", fr: "Autriche", de: "Österreich" },
  { code: "PT", it: "Portogallo", en: "Portugal", es: "Portugal", fr: "Portugal", de: "Portugal" },
  { code: "GR", it: "Grecia", en: "Greece", es: "Grecia", fr: "Grèce", de: "Griechenland" },
  { code: "GB", it: "Regno Unito", en: "United Kingdom", es: "Reino Unido", fr: "Royaume-Uni", de: "Vereinigtes Königreich" },
  { code: "CH", it: "Svizzera", en: "Switzerland", es: "Suiza", fr: "Suisse", de: "Schweiz" },
  { code: "US", it: "Stati Uniti", en: "United States", es: "Estados Unidos", fr: "États-Unis", de: "Vereinigte Staaten" },
];

export const countryName = (code, lang = "it") => {
  const row = COUNTRIES.find((c) => c.code === String(code || "IT").toUpperCase());
  return row ? row[lang] || row.en : String(code || "");
};

/**
 * L'identificativo locale di un paese, per il campo da compilare nella scheda.
 *
 * Gli `id` sono quelli di `JURISDICTIONS[...]["identifiers"]`: il backend
 * rifiuta un identificativo che quel paese non prevede, quindi un errore qui
 * diventa un 400 con il nome giusto invece di un dato inutile sul documento.
 * Germania e Austria non ne hanno uno nazionale (la licenza è comunale) e non
 * compaiono: gli altri tre mercati nuovi hanno un identificativo facoltativo,
 * perché la regola è del consiglio locale o del cantone, non dello Stato.
 */
export const LOCAL_IDENTIFIER = {
  ES: { id: "registro_turistico", it: "Numero di registro turistico", en: "Tourist registration number", es: "Número de registro turístico", fr: "Numéro d'enregistrement touristique", de: "Touristenregistriernummer" },
  FR: { id: "numero_enregistrement", it: "Numero di registrazione comunale (13 caratteri)", en: "Municipal registration number (13 characters)", es: "Número de registro municipal (13 caracteres)", fr: "Numéro d'enregistrement municipal (13 caractères)", de: "Kommunale Registriernummer (13 Zeichen)" },
  PT: { id: "rnal", it: "RNAL", en: "RNAL", es: "RNAL", fr: "RNAL", de: "RNAL" },
  GR: { id: "ama", it: "AMA", en: "AMA", es: "AMA", fr: "AMA", de: "AMA" },
  GB: { id: "council_licence", it: "Licenza o registrazione del consiglio locale (Scozia e Galles)", en: "Local council licence or registration (Scotland and Wales)", es: "Licencia o registro del ayuntamiento (Escocia y Gales)", fr: "Licence ou enregistrement du conseil local (Écosse et Pays de Galles)", de: "Lizenz oder Registrierung des Gemeinderats (Schottland und Wales)" },
  CH: { id: "cantonal_permit", it: "Autorizzazione cantonale per l'affitto breve", en: "Cantonal permit for short-term letting", es: "Autorización cantonal para alquileres cortos", fr: "Autorisation cantonale pour la location de courte durée", de: "Kantonale Bewilligung für Kurzzeitvermietung" },
  US: { id: "local_permit", it: "Licenza o numero di registrazione locale", en: "Local short-term rental licence or registration number", es: "Licencia o número de registro local", fr: "Licence ou numéro d'enregistrement local", de: "Örtliche Lizenz oder Registrierungsnummer" },
};

/** Il paese della struttura, con l'Italia come default per i documenti vecchi. */
export const propertyCountry = (property) => {
  const raw = String(property?.country || "IT").trim().toUpperCase();
  return COUNTRIES.some((c) => c.code === raw) ? raw : "IT";
};
